import { graphql } from '@/utils/graphql';
import type { Conversations } from '@/components/Chat/types';
import { fitMessages } from './insightConversationCodec';
import { parseAwsJson } from './awsJson';
import type { InsightTraces } from '@/models/insight';

// AI Insights conversations in DynamoDB (InsightConversation, owner-only), per user and workspace.
// The repo in pages/Insights/data keeps the UI's interface over these calls.

export type InsightConversationRow = {
  id: string;
  userId: string;
  workspaceId: string;
  title: string;
  messages: string;
  messageCount: number;
  traces?: string | null;
  projectId?: string | null;
  originKey?: string | null;
  sessionId?: string | null;
  shared?: boolean | null;
  createdByName?: string | null;
  lastActivityAt: string;
  createdAt?: string;
  updatedAt?: string;
};

export type InsightConversationFields = {
  title: string;
  messages: Conversations;
  traces?: InsightTraces;
  projectId?: string | null;
  originKey?: string | null;
  sessionId?: string | null;
  shared?: boolean;
  createdByName?: string | null;
  lastActivityAt: number;
};

const toInput = (fields: Partial<InsightConversationFields>) => {
  const input: Record<string, unknown> = {};
  if (fields.title !== undefined) input.title = fields.title;
  if (fields.messages !== undefined) {
    input.messages = fitMessages(fields.messages);
    input.messageCount = fields.messages.length;
  }
  if (fields.traces !== undefined) input.traces = JSON.stringify(fields.traces ?? {});
  if (fields.projectId !== undefined) input.projectId = fields.projectId;
  if (fields.originKey !== undefined) input.originKey = fields.originKey;
  if (fields.sessionId !== undefined) input.sessionId = fields.sessionId;
  if (fields.shared !== undefined) input.shared = fields.shared;
  if (fields.createdByName !== undefined) input.createdByName = fields.createdByName;
  if (fields.lastActivityAt !== undefined)
    input.lastActivityAt = new Date(fields.lastActivityAt).toISOString();
  return input;
};

// The generated operations are not in the typed command maps of @/api (hand-edited, not
// regenerated), so they go through graphql() untyped, as embedToken does.
const call = <T>(command: string, variables: Record<string, unknown>): Promise<T> =>
  graphql(command as unknown as never, variables as unknown as never) as unknown as Promise<T>;

export default class InsightConversationStore {
  static async listForUser(userId: string, workspaceId: string): Promise<InsightConversationRow[]> {
    // Pages one after another: each needs the previous one's token.
    const page = async (nextToken?: string | null): Promise<InsightConversationRow[]> => {
      const rs = await call<{ items: InsightConversationRow[]; nextToken?: string | null }>(
        'listInsightConversationsByUser',
        {
          userId,
          workspaceId: { eq: workspaceId },
          limit: 100,
          nextToken,
        },
      );
      const items = (rs?.items ?? []).filter(Boolean);
      return rs?.nextToken ? items.concat(await page(rs.nextToken)) : items;
    };
    return page();
  }

  static async create(
    id: string,
    userId: string,
    workspaceId: string,
    fields: InsightConversationFields,
  ): Promise<InsightConversationRow> {
    return call('createInsightConversation', {
      input: { id, userId, workspaceId, ...toInput(fields) },
    });
  }

  static async update(
    id: string,
    fields: Partial<InsightConversationFields>,
  ): Promise<InsightConversationRow> {
    return call('updateInsightConversation', { input: { id, ...toInput(fields) } });
  }

  static async remove(id: string): Promise<void> {
    await call('deleteInsightConversation', { input: { id } });
  }

  /**
   * What other members of the workspace shared: conversations, and the projects they belong to.
   * The conversations come without their messages and traces; `getShared` reads one whole.
   */
  static async listShared(
    workspaceId: string,
  ): Promise<{ conversations: InsightConversationRow[]; projects: Record<string, unknown>[] }> {
    const rs = await call<{ conversations: string; projects: string }>(
      'beemAgentListSharedInsights',
      {
        query: { workspaceId },
      },
    );
    const parse = (v: string | undefined) => {
      try {
        const parsed = parseAwsJson<unknown>(v || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };
    return { conversations: parse(rs?.conversations), projects: parse(rs?.projects) };
  }

  /** One conversation another member shared, messages included (the shared list has none). */
  static async getShared(id: string, workspaceId: string): Promise<InsightConversationRow> {
    const rs = await call<{ conversation: unknown }>('beemAgentGetSharedInsightConversation', {
      query: { id, workspaceId },
    });
    return parseAwsJson<InsightConversationRow>(rs.conversation);
  }
}

export { decodeMessages, encodeMessages, fitMessages } from './insightConversationCodec';
